// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * First-party base64-VLQ and `mappings` decoder, per ECMA-426 (the TC39 Source Map spec; note
 * https://tc39.es/source-map/ now 301s to https://tc39.es/ecma426/).
 *
 * WHY NOT A LIBRARY. Both obvious candidates were measured and both are disqualifying for a
 * security tool that ingests untrusted maps in a browser tab:
 *
 *   - `@jridgewell/sourcemap-codec@1.5.5` SILENTLY PROMOTES MALFORMED SEGMENTS. `decode("AA")` and
 *     `decode("AAA")` both return `[[[0,0,0,0]]]`. Per spec, 2- and 3-field segments are invalid;
 *     what the library returns instead is a FABRICATED attribution to `sources[0]:1:0`.
 *   - `source-map@0.8.0` CRASHES OUTRIGHT on out-of-range indices (`Error: No element indexed by 2`)
 *     and returns the literal string `"null"` as a source name for a spec-allowed `null` entry.
 *
 * THE BUG THAT ACTUALLY SHIPS IN HAND-WRITTEN DECODERS IS THE ACCUMULATOR RESET — see below. It is
 * INVISIBLE on a minified single-line bundle, because there are no semicolons at all, and explodes
 * on the first unminified or line-limited build. Both exist in the wild: `vite build --minify false`
 * gives 43 generated lines and 42 semicolons; `esbuild --minify --line-limit=60` gives 4 lines.
 */

const B64 = new Int8Array(128).fill(-1);
{
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  for (let i = 0; i < alphabet.length; i++) B64[alphabet.charCodeAt(i)] = i;
}

export class MalformedMapError extends Error {
  constructor(readonly reason: string) {
    super(`malformed source map: ${reason}`);
    this.name = 'MalformedMapError';
  }
}

/**
 * A VLQ may legally span SEVEN groups, not six: 33 bits of payload = a 32-bit magnitude plus the
 * sign bit. Measured — `2^29` encodes as `"ggggggB"` (7), `2^30-1` as `"+/////B"` (7), `2^31-1` as
 * `"+/////D"` (7). A decoder that rejects at 6 groups rejects valid maps.
 */
const MAX_GROUPS = 7;

interface VlqCursor {
  i: number;
}

function decodeVlq(s: string, cur: VlqCursor): number {
  let value = 0;
  let shift = 0;
  let groups = 0;
  let byte: number;
  do {
    if (cur.i >= s.length) throw new MalformedMapError('VLQ truncated');
    const code = s.charCodeAt(cur.i++);
    byte = code < 128 ? B64[code]! : -1;
    if (byte < 0) throw new MalformedMapError(`invalid base64 character at ${cur.i - 1}`);
    if (++groups > MAX_GROUPS) throw new MalformedMapError('VLQ longer than 7 groups');
    value += (byte & 31) * 2 ** shift;
    shift += 5;
  } while (byte & 32);

  if (value > 0xffffffff) throw new MalformedMapError('VLQ magnitude exceeds 32 bits');
  const negative = (value & 1) === 1;
  const magnitude = Math.floor(value / 2);
  // Normalise -0. `"B"` decodes to raw bits 1 -> sign set, magnitude 0, which would produce -0 and
  // poison both comparisons and JSON.stringify.
  if (magnitude === 0) return 0;
  return negative ? -magnitude : magnitude;
}

/** One decoded segment. 1-field segments mark generated code as explicitly UNMAPPED. */
export interface Segment {
  readonly generatedColumn: number;
  readonly sourceIndex?: number;
  readonly originalLine?: number;
  readonly originalColumn?: number;
  readonly nameIndex?: number;
}

export interface DecodedMappings {
  /** One array of segments per generated line, in ascending generated-column order. */
  readonly lines: Segment[][];
  readonly segmentCount: number;
}

/**
 * Decode a `mappings` string.
 *
 * THE RESET RULE — this is the whole file in three lines:
 *
 *     At a ';':  generatedColumn = 0.
 *                sourceIndex, originalLine, originalColumn, nameIndex DO NOT RESET.
 *                They persist for the whole map. A new sourceIndex does NOT reset originalLine.
 *
 * Measured against `@jridgewell/sourcemap-codec` on a 43-line unminified map: decoding with only
 * generatedColumn resetting matched the reference exactly; resetting all five diverged first at
 * generated line 33 (`[0,0,1,0]` vs `[0,0,1,-21]`) and produced 11 negative columns.
 *
 * CANARY: after every segment, `originalLine`, `originalColumn` and `sourceIndex` must all be >= 0.
 * Nothing legal produces a negative value there, so this single assertion catches the reset bug for
 * free, on the user's own file, even on shapes our fixtures do not cover.
 */
export function decodeMappings(mappings: string, sourceCount: number, nameCount: number): DecodedMappings {
  const lines: Segment[][] = [];
  let current: Segment[] = [];

  let generatedColumn = 0;
  let sourceIndex = 0;
  let originalLine = 0;
  let originalColumn = 0;
  let nameIndex = 0;
  let segmentCount = 0;

  const cur: VlqCursor = { i: 0 };
  const n = mappings.length;

  while (cur.i <= n) {
    if (cur.i === n) {
      lines.push(current);
      break;
    }
    const c = mappings[cur.i];
    if (c === ';') {
      cur.i++;
      lines.push(current);
      current = [];
      generatedColumn = 0; // ...and ONLY this one.
      continue;
    }
    if (c === ',') {
      cur.i++;
      continue;
    }

    // Count the fields in this segment before consuming, so an invalid count is rejected rather
    // than silently promoted the way @jridgewell/sourcemap-codec does.
    const start = cur.i;
    let fields = 0;
    const probe: VlqCursor = { i: start };
    while (probe.i < n && mappings[probe.i] !== ',' && mappings[probe.i] !== ';') {
      decodeVlq(mappings, probe);
      fields++;
      if (fields > 5) throw new MalformedMapError('segment has more than 5 fields');
    }
    if (fields !== 1 && fields !== 4 && fields !== 5) {
      throw new MalformedMapError(`segment has ${fields} fields; only 1, 4 and 5 are valid`);
    }

    generatedColumn += decodeVlq(mappings, cur);
    if (generatedColumn < 0) throw new MalformedMapError('negative generated column');

    if (fields === 1) {
      current.push({ generatedColumn });
      segmentCount++;
      continue;
    }

    sourceIndex += decodeVlq(mappings, cur);
    originalLine += decodeVlq(mappings, cur);
    originalColumn += decodeVlq(mappings, cur);

    // The canary.
    if (sourceIndex < 0 || originalLine < 0 || originalColumn < 0) {
      throw new MalformedMapError('negative source index, line or column — the map is corrupt');
    }
    if (sourceIndex >= sourceCount) {
      throw new MalformedMapError(`sourceIndex ${sourceIndex} is beyond ${sourceCount} sources`);
    }

    if (fields === 5) {
      nameIndex += decodeVlq(mappings, cur);
      if (nameIndex < 0 || nameIndex >= nameCount) {
        throw new MalformedMapError(`nameIndex ${nameIndex} is beyond ${nameCount} names`);
      }
      current.push({ generatedColumn, sourceIndex, originalLine, originalColumn, nameIndex });
    } else {
      current.push({ generatedColumn, sourceIndex, originalLine, originalColumn });
    }
    segmentCount++;
  }

  return { lines, segmentCount };
}

/** Exposed for the fixture suite: decode one VLQ from the start of a string. */
export function decodeSingleVlq(s: string): number {
  return decodeVlq(s, { i: 0 });
}
